import { AbstractComponent } from "appworks/components/abstract-component";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Sequence } from "appworks/utils/contracts/sequence";
import { RandomRangeFloat, RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { FowlSoundEvents } from "setup/fowl-sound-events";

/**
 * Executes the callback function after a random delay between the min and max durations (ms).
 **/
const randomTimeout = (callback: () => void, minDuration: number, maxDuration: number) =>
    Timer.setTimeout(() => callback(), RandomRangeInt(minDuration, maxDuration));

type persistentSceneType = keyof typeof gameLayers.BackgroundPersistentAnimation.scene;
type intermittentSceneType = keyof typeof gameLayers.BackgroundIntermittentAnimation.scene;

export class FowlBackgroundFXComponent extends AbstractComponent {
    protected persistentLayer: typeof gameLayers.BackgroundPersistentAnimation;
    protected intermittentLayer: typeof gameLayers.BackgroundIntermittentAnimation;

    protected duration = 30000;

    public constructor() {
        super();

        this.persistentLayer = gameLayers.BackgroundPersistentAnimation;
        this.intermittentLayer = gameLayers.BackgroundIntermittentAnimation;

        this.persistentLayer.onSceneEnter.add((scene: persistentSceneType) => this.persistentOnSceneEnter(scene));
        this.intermittentLayer.onSceneEnter.add((scene: intermittentSceneType) => this.intermittentOnSceneEnter(scene));
    }

    protected persistentOnSceneEnter(scene: persistentSceneType): void {
        if (scene !== "default") {
            return;
        }

        const ss = Services.get(SoundService);

        // NOTE: we want to essentially play the bird animation on loop, but add a delay between consecutive loops
        // achieve by using recursion and setTimeout callbacks
        const birdsLoop = () => {
            if (!this.persistentLayer.currentSceneIs("default")) return;
            const birds = this.persistentLayer.getSpine("birds");
            if (birds) {
                ss.customEvent(FowlSoundEvents.geese_flying);
                birds.playOnce("birds_back", true).then(() => {
                    randomTimeout(birdsLoop, this.duration, this.duration * 2);
                });
            }
        };
        // animate birds
        randomTimeout(birdsLoop, 0, this.duration * 2);

        // Banjo sfx
        const playBanjo = () => {
            if (!this.persistentLayer.currentSceneIs("default")) return;
            if (uiFlags.has(UIFlag.IDLE) && !uiFlags.has(UIFlag.BONUS)) {
                ss.customEvent(FowlSoundEvents.banjo_playing);
            }
            randomTimeout(playBanjo, this.duration * 2, this.duration * 3);
        };
        randomTimeout(playBanjo, 0, this.duration * 3);

        // add clouds
        const clouds: Sprite[] = [];
        while (clouds.length < 12) {
            const cloud = Services.get(GraphicsService).createSprite("bg_cloud_" + RandomRangeInt(1, 5));
            this.persistentLayer.add(cloud, false, true);
            clouds.push(cloud);

            cloud.landscape.x = -cloud.landscape.width;
            const randomiseCloudY = () => {
                cloud.landscape.y = RandomRangeInt(-1920, 300);
                cloud.landscape.scale.set(RandomRangeFloat(0.6, 1));
                cloud.portrait.set(cloud.landscape.scale.x);
            };

            const duration = RandomRangeInt(60000, 120000);
            new Tween(cloud.landscape)
                .to({ x: this.persistentLayer.bounds.landscape.width }, duration)
                .repeat(Infinity)
                .onStart(randomiseCloudY)
                .onLoop(randomiseCloudY)
                .onUpdate(() => cloud.portrait.setPosition(cloud.landscape))
                .start()
                .delayOffset(-RandomRangeInt(0, duration));
        }

        // orange bird flying in portrait
        const orangeBird = this.persistentLayer.scene.default.contents.bg_orange_bird;
        const orangeBirdLoop = () => {
            if (!this.persistentLayer.currentSceneIs("default")) return;
            orangeBird.playOnce("fly_animation_portrait").then(() => randomTimeout(orangeBirdLoop, this.duration, this.duration * 1.5));
        };
        randomTimeout(orangeBirdLoop, 0, this.duration * 1.5);

        // hide static tree (only used for transition)
        const staticTree = this.persistentLayer.getSprite("tree_static");
        if (staticTree) staticTree.visible = false;
    }

    protected intermittentOnSceneEnter(scene: intermittentSceneType): void {
        if (scene !== "default") return;
        const ss = Services.get(SoundService);
        // Tractor
        const tractorLoop = () => {
            if (!this.intermittentLayer.currentSceneIs("default")) return;
            const tractor = this.intermittentLayer.getSpine("tractor");
            if (tractor) {
                const playSFX = Services.get(CanvasService).orientation === Orientation.LANDSCAPE;
                if (playSFX) {
                    ss.customEvent(FowlSoundEvents.tractor_driving);
                }
                tractor.playOnce("animation", true).then(() => {
                    ss.customEvent(FowlSoundEvents.tractor_stop);
                    randomTimeout(tractorLoop, this.duration, this.duration * 2);
                });
            }
        };
        randomTimeout(tractorLoop, 0, this.duration * 2);

        // Bird/Rabbit
        const animalLoop = () => {
            if (!this.intermittentLayer.currentSceneIs("default")) return;
            const rand = RandomRangeInt(0, 1);
            const { rabbit, orange_bird } = gameLayers.BackgroundIntermittentAnimation.scene.default.contents;
            if (rand === 0) {
                new Sequence([
                    () => orange_bird.playOnce("land_animation"),
                    () => orange_bird.playOnce("idle_animation"),
                    () => orange_bird.playOnce("fly_animation"),
                ]).then(() => {
                    randomTimeout(animalLoop, this.duration, this.duration * 1.5);
                });
            } else {
                rabbit.playOnce("animation").then(() => {
                    randomTimeout(animalLoop, this.duration, this.duration * 1.5);
                });
            }
        };
        randomTimeout(animalLoop, 500, this.duration * 1.5);
    }
}
